import React from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import clsx from 'clsx';
import { FiAlertCircle, FiSearch, FiEye, FiCheckCircle } from 'react-icons/fi';
import { Incident, IncidentStatus, IncidentSeverity } from '../../types';

interface IncidentTimelineProps {
  incidents: Incident[];
  isLoading: boolean;
  onViewMore?: () => void;
}

const statusIcon: Record<IncidentStatus, React.ReactNode> = {
  investigating: <FiSearch className="w-4 h-4" />,
  identified: <FiAlertCircle className="w-4 h-4" />,
  monitoring: <FiEye className="w-4 h-4" />,
  resolved: <FiCheckCircle className="w-4 h-4" />,
};

const statusColor: Record<IncidentStatus, string> = {
  investigating: 'bg-red-500',
  identified: 'bg-amber-500',
  monitoring: 'bg-blue-500',
  resolved: 'bg-green-500',
};

const statusLabel: Record<IncidentStatus, string> = {
  investigating: 'Investigating',
  identified: 'Identified',
  monitoring: 'Monitoring',
  resolved: 'Resolved',
};

const severityBadge: Record<IncidentSeverity, { bg: string; text: string }> = {
  minor: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  major: { bg: 'bg-orange-100', text: 'text-orange-800' },
  critical: { bg: 'bg-red-100', text: 'text-red-800' },
};

const IncidentTimeline: React.FC<IncidentTimelineProps> = ({
  incidents,
  isLoading,
  onViewMore,
}) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-48 mb-6"></div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="mb-6">
              <div className="h-4 bg-gray-200 rounded w-64 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-48"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-6">
        Recent Incidents
      </h2>

      {incidents.length === 0 ? (
        <div className="text-center py-8">
          <FiCheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <p className="text-gray-600">No incidents reported in the last 90 days</p>
          <p className="text-sm text-gray-400 mt-1">All systems have been running smoothly</p>
        </div>
      ) : (
        <div className="space-y-6">
          {incidents.map((incident) => (
            <div
              key={incident.id}
              className="relative pl-6 border-l-2 border-gray-200"
            >
              {/* Status indicator */}
              <div
                className={clsx(
                  'absolute -left-2 top-0 w-4 h-4 rounded-full flex items-center justify-center text-white',
                  statusColor[incident.status]
                )}
              >
                {statusIcon[incident.status]}
              </div>

              {/* Incident header */}
              <div className="mb-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-medium text-gray-900">{incident.title}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={clsx(
                          'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                          severityBadge[incident.severity].bg,
                          severityBadge[incident.severity].text
                        )}
                      >
                        {incident.severity.charAt(0).toUpperCase() + incident.severity.slice(1)}
                      </span>
                      <span className="text-xs text-gray-500">
                        {statusLabel[incident.status]}
                      </span>
                    </div>
                  </div>
                  <span className="text-sm text-gray-400 whitespace-nowrap">
                    {formatDistanceToNow(new Date(incident.startedAt), { addSuffix: true })}
                  </span>
                </div>

                {/* Affected services */}
                {incident.affectedServices.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {incident.affectedServices.map((service) => (
                      <span
                        key={service}
                        className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-xs text-gray-600"
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Updates timeline */}
              <div className="space-y-3 ml-2">
                {incident.updates.slice(0, 3).map((update, index) => (
                  <div key={update.id} className="relative">
                    <div
                      className={clsx(
                        'absolute -left-4 top-1.5 w-2 h-2 rounded-full',
                        index === 0 ? statusColor[update.status] : 'bg-gray-300'
                      )}
                    />
                    <div className="text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">
                          {statusLabel[update.status]}
                        </span>
                        <span className="text-gray-400">
                          {format(new Date(update.createdAt), 'MMM d, h:mm a')}
                        </span>
                      </div>
                      <p className="text-gray-600 mt-0.5">{update.message}</p>
                    </div>
                  </div>
                ))}

                {incident.updates.length > 3 && (
                  <button
                    type="button"
                    className="text-sm text-brand-600 hover:text-brand-700"
                  >
                    View {incident.updates.length - 3} more updates
                  </button>
                )}
              </div>

              {/* Resolution time */}
              {incident.resolvedAt && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-400">
                    Resolved on {format(new Date(incident.resolvedAt), 'MMM d, yyyy h:mm a')}
                    {' '}
                    (Duration: {formatDistanceToNow(new Date(incident.startedAt), {
                      includeSeconds: true,
                    }).replace(' ago', '')})
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {onViewMore && incidents.length > 0 && (
        <button
          type="button"
          onClick={onViewMore}
          className="mt-6 w-full py-2 text-sm text-brand-600 hover:text-brand-700 font-medium border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          View incident history
        </button>
      )}
    </div>
  );
};

export default IncidentTimeline;
